import type { AgentTicketOptions, TicketDetail } from "@noverik/contracts";
import {
  ArrowLeft,
  CalendarDays,
  CircleUserRound,
  Clock3,
  Inbox,
  Paperclip,
  Download,
  Eye,
  Send,
  UsersRound,
  X,
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { AgentTicketActions } from "../components/AgentTicketActions";
import { RichTextEditor, hasRichTextContent } from "../components/RichTextEditor";
import { apiRequest, ApiRequestError } from "../lib/api";
import { encodeAttachments } from "../lib/files";

type Props = { mode?: "client" | "agent" };

const dateFormatter = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : dateFormatter.format(date);
}

export function TicketDetailPage({ mode = "client" }: Props) {
  const { ticketId = "" } = useParams();
  const { session } = useAuth();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [agentOptions, setAgentOptions] = useState<AgentTicketOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const back = mode === "agent" ? "/desk/chamados" : "/chamados";

  const loadAgentOptions = useCallback(async () => {
    const next = await apiRequest<AgentTicketOptions>(
      "/tickets/" + encodeURIComponent(ticketId) + "/agent/options",
    );
    setAgentOptions(next);
    return next;
  }, [ticketId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const next = await apiRequest<TicketDetail>(
        "/tickets/" + encodeURIComponent(ticketId),
      );
      setTicket(next);

      if (mode === "agent") {
        await loadAgentOptions();
      } else {
        setAgentOptions(null);
      }
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Não foi possível carregar o chamado.",
      );
    } finally {
      setLoading(false);
    }
  }, [ticketId, mode, loadAgentOptions]);

  useEffect(() => {
    void load();
  }, [load]);

  const submitReply = async (event: FormEvent) => {
    event.preventDefault();

    if (!session?.csrfToken || !ticket) {
      setReplyError("Sua sessão expirou. Atualize a página e tente novamente.");
      return;
    }

    if (!hasRichTextContent(reply)) {
      setReplyError("Digite uma mensagem antes de enviar.");
      return;
    }

    setSending(true);
    setReplyError(null);

    try {
      const attachments = await encodeAttachments(files);
      const updated = await apiRequest<TicketDetail>(
        "/tickets/" + encodeURIComponent(ticket.number) + "/replies",
        {
          method: "POST",
          csrfToken: session.csrfToken,
          body: {
            message: reply,
            attachments,
          },
        },
      );

      setTicket(updated);
      setReply("");
      setFiles([]);
    } catch (err) {
      setReplyError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : "Não foi possível publicar a resposta.",
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="page"><div className="data-message">Carregando atendimento...</div></div>;
  }

  if (error || !ticket) {
    return (
      <div className="page">
        <Link className="back-link" to={back}><ArrowLeft size={16} /> Voltar aos chamados</Link>
        <div className="data-message error">
          <strong>Chamado indisponível.</strong>
          <span>{error || "Chamado não encontrado."}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page ticket-detail-page">
      <Link className="back-link" to={back}><ArrowLeft size={16} /> Voltar aos chamados</Link>

      <div className="ticket-detail-heading">
        <div>
          <div className="ticket-kicker">
            <span className={"status status-" + ticket.status.state}>{ticket.status.label}</span>
            {ticket.overdue && (
              <span className="status status-overdue" title="Prazo SLA excedido">Atrasado</span>
            )}
            <span>Chamado #{ticket.number}</span>
          </div>
          <h1>{ticket.subject}</h1>
        </div>
      </div>

      {mode === "agent" && agentOptions && (
        <AgentTicketActions
          ticket={ticket}
          options={agentOptions}
          reloadOptions={loadAgentOptions}
          onUpdated={(updated, options) => {
            setTicket(updated);
            if (options) setAgentOptions(options);
          }}
        />
      )}

      <section className="ticket-meta-grid">
        <div className="meta-card">
          <span className="meta-icon blue"><Inbox size={20} /></span>
          <div>
            <small>Atendimento</small>
            <strong>{ticket.department || "Sem departamento"}</strong>
            <span>{ticket.priority || "Prioridade não informada"}</span>
          </div>
        </div>
        <div className="meta-card">
          <span className="meta-icon green"><CircleUserRound size={20} /></span>
          <div>
            <small>Solicitante</small>
            <strong>{ticket.requester.name}</strong>
            <span>{ticket.requester.email}</span>
          </div>
        </div>
        <div className="meta-card">
          <span className="meta-icon purple"><UsersRound size={20} /></span>
          <div>
            <small>Responsável</small>
            <strong>{ticket.assignee?.name || "Não atribuído"}</strong>
            <span>{ticket.assignee?.type === "team" ? "Equipe" : ticket.assignee ? "Agente" : "Aguardando atribuição"}</span>
          </div>
        </div>
        <div className="meta-card">
          <span className="meta-icon amber"><CalendarDays size={20} /></span>
          <div>
            <small>Atualização</small>
            <strong>{formatDate(ticket.updatedAt)}</strong>
            <span>Criado em {formatDate(ticket.createdAt)}</span>
          </div>
        </div>
      </section>

      <section className="thread-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">HISTÓRICO</span>
            <h2>Histórico do atendimento</h2>
          </div>
          <div className="thread-count"><Clock3 size={15} /> {ticket.thread.length} registro{ticket.thread.length === 1 ? "" : "s"}</div>
        </div>

        <div className="thread-list">
          {ticket.thread.length === 0 && (
            <div className="table-empty">
              <strong>Sem mensagens visíveis.</strong>
            </div>
          )}

          {ticket.thread.map((entry) => (
            <article
              key={entry.id}
              className={"thread-item " + (
                entry.type === "message" && entry.author.role === "agent"
                  ? "response"
                  : entry.type
              )}
            >
              <div className="thread-avatar">
                {entry.author.name.slice(0, 1).toUpperCase() || "N"}
              </div>
              <div className="thread-card">
                <header>
                  <div>
                    <strong>{entry.author.name || "Sistema"}</strong>
                    <span>
                      {entry.type === "note"
                        ? "Nota interna"
                        : entry.type === "message" && entry.author.role === "agent"
                          ? "Chamado aberto pelo agente"
                          : entry.author.role === "agent"
                            ? "Equipe Noverik"
                            : entry.author.role === "client"
                              ? "Cliente"
                              : "Sistema"}
                    </span>
                  </div>
                  <time>{formatDate(entry.createdAt)}</time>
                </header>
                <div
                  className="thread-body thread-body-html"
                  dangerouslySetInnerHTML={{ __html: entry.bodyHtml || "—" }}
                />
                {entry.attachments.length > 0 && (
                  <footer className="thread-attachments">
                    <span className="thread-attachments-title">
                      <Paperclip size={14} />
                      Anexos ({entry.attachments.length})
                    </span>
                    <div className="thread-attachment-list">
                      {entry.attachments.map((attachment) => (
                        <a
                          key={attachment.id}
                          className="thread-attachment"
                          href={attachment.url}
                          download={attachment.name}
                          title={"Baixar " + attachment.name}
                        >
                          <span className="thread-attachment-icon"><Paperclip size={17} /></span>
                          <span className="thread-attachment-info">
                            <strong>{attachment.name}</strong>
                            <small>{Math.max(1, Math.ceil(attachment.size / 1024))} KB</small>
                          </span>
                          <Download size={17} className="thread-attachment-download" />
                        </a>
                      ))}
                      {entry.attachments.filter((attachment) => attachment.type.startsWith("image/")).map((attachment) => (
                        <a
                          key={"preview-" + attachment.id}
                          className="thread-attachment-preview"
                          href={attachment.url + "?inline=1"}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={"Visualizar " + attachment.name}
                          aria-label={"Visualizar " + attachment.name}
                        >
                          <Eye size={17} />
                          Visualizar
                        </a>
                      ))}
                    </div>
                  </footer>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {mode === "client" && (
        <section className="reply-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">RESPONDER</span>
              <h2>Responder chamado</h2>
            </div>
          </div>

          <form className="reply-card" onSubmit={submitReply}>
            <label className="field">
              <span>Sua mensagem</span>
              <RichTextEditor
                value={reply}
                onChange={setReply}
                placeholder="Escreva sua resposta com o máximo de detalhes possível..."
                label="Sua mensagem"
                minHeight={180}
                disabled={sending}
              />
            </label>

            <div className="reply-files">
              <label className="button secondary file-button">
                <Paperclip size={16} />
                Anexar arquivos
                <input
                  type="file"
                  multiple
                  onChange={(event) => {
                    const selected = Array.from(event.target.files ?? []);
                    setFiles((current) => [...current, ...selected].slice(0, 10));
                    event.target.value = "";
                  }}
                />
              </label>

              {files.map((file, index) => (
                <span className="reply-file" key={file.name + file.lastModified + index}>
                  {file.name}
                  <button
                    type="button"
                    onClick={() => setFiles((current) => current.filter((_, position) => position !== index))}
                    aria-label={"Remover " + file.name}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>

            {ticket.status.state === "closed" && (
              <div className="reopen-notice">
                Este chamado será reaberto quando você publicar a resposta.
              </div>
            )}

            {replyError && <div className="login-message form-error">{replyError}</div>}

            <div className="form-actions">
              <button className="button primary" disabled={sending}>
                <Send size={16} />
                {sending ? "Publicando..." : "Publicar resposta"}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
